<template>
  <div class="records-page">
    <div class="header" :style="headerStyle">
      <div class="header-hotel">
        <img
          v-if="brandUrl"
          :src="brandUrl"
          class="hotel-logo"
          @error="brandUrl = ''"
        />
        <span class="hotel-name">{{ hotelName }}</span>
      </div>
    </div>

    <!-- <div class="summary" v-if="summaryText">
      {{ summaryText }}
    </div> -->

    <div class="records-content">
      <van-empty v-if="!loading && records.length === 0" description="No requests found" />

      <div v-for="item in records" :key="item.id" class="record-card">
        <div class="record-top">
          <div class="record-type">{{ requestTypeLabel(item.requestType) }}</div>
          <div class="status-group">
            <span class="status-tag" :class="approvalStatusClass(item.approvalStatus)">
              {{ approvalStatusLabel(item.approvalStatus) }}
            </span>
            <!-- <span class="status-tag status-delivery" :class="deliveryStatusClass(item.notifyStatus)">
              {{ deliveryStatusLabel(item.notifyStatus) }}
            </span> -->
          </div>
        </div>

        <div class="record-row"><span>Department</span><span>{{ item.deptName || '-' }}</span></div>
        <div class="record-row"><span>Date</span><span>{{ formatDateRange(item.startDate, item.endDate) }}</span></div>
        <div class="record-row"><span>Time</span><span>{{ formatTimeRange(item.startTime, item.endTime) }}</span></div>
        <div class="record-row"><span>On Call</span><span>{{ item.onCall ? 'Yes' : 'No' }}</span></div>
        <div class="record-row"><span>Submitted</span><span>{{ formatSubmitTime(item.createTime) }}</span></div>
        <div class="record-row" v-if="item.notes"><span>Notes</span><span class="notes">{{ item.notes }}</span></div>
      </div>

      <van-pagination
        v-if="total > pageSize"
        v-model="pageNum"
        :total-items="total"
        :items-per-page="pageSize"
        mode="simple"
        @change="fetchRecords"
      />
    </div>

    <div class="action-area">
      <van-button round block plain type="info" @click="goBackToForm">
        Back To Schedule Assist
      </van-button>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { Toast } from "vant";
import { getCurrentScheduleUser, getHotelInfo, scheduleRecordList } from "@/api/schedule";
import { getScheduleAuthToken, getScheduleAuthUserId, setScheduleAuthUserId } from "@/utils/scheduleAuth";

export default {
  name: "ScheduleRecords",
  data() {
    return {
      hotelNo: "",
      hotelName: "",
      brandUrl: "",
      hotelColor: "",
      records: [],
      loading: false,
      pageNum: 1,
      pageSize: 5,
      total: 0,
      requesterUserId: null,
      anonymousEmployeeName: "",
      anonymousDeptId: null,
      anonymousDeptName: "",
    };
  },
  computed: {
    headerStyle() {
      if (this.hotelColor) {
        return {
          background: `#${this.hotelColor}`,
        };
      }
      return {};
    },
    summaryText() {
      if (this.requesterUserId) {
        return "Showing requests for the current signed-in user.";
      }
      if (this.anonymousEmployeeName && this.anonymousDeptName) {
        return `${this.anonymousEmployeeName} · ${this.anonymousDeptName}`;
      }
      return "";
    },
  },
  async created() {
    this.hotelNo = this.$route.query.hotel || "";
    if (!this.hotelNo) {
      Toast.fail("Missing hotel information. Please scan the QR code again.");
      return;
    }
    await this.initializePage();
  },
  methods: {
    async initializePage() {
      await this.loadHotelInfo();
      const canLoadRecords = await this.resolveRecordScope();
      if (!canLoadRecords) {
        return;
      }
      await this.fetchRecords();
    },
    async loadHotelInfo() {
      try {
        const res = await getHotelInfo(this.hotelNo);
        const isSuccess = Number(res.status) === 0 || res.success === true;
        if (isSuccess && res.data) {
          this.hotelName = res.data.hotelName || "";
          this.hotelColor = res.data.color || "";
          if (res.data.brand) {
            this.brandUrl =
              "https://hotelfile353485474178.s3-accelerate.amazonaws.com/logo/" +
              res.data.brand.toLowerCase().replace(/ /g, "_") +
              ".png";
          }
        }
      } catch (e) {
        // Keep page usable without branding.
      }
    },
    async resolveRecordScope() {
      if (this.$route.query.user) {
        this.requesterUserId = this.$route.query.user;
        return true;
      }
      if (this.$route.query.login === "1") {
        let userId = getScheduleAuthUserId();
        if (!userId) {
          const token = getScheduleAuthToken();
          if (!token) {
            Toast.fail("Login session expired. Please log in again.");
            this.goToLogin();
            return false;
          }
          const currentUserRes = await getCurrentScheduleUser(token);
          const isSuccess = Number(currentUserRes.status) === 0 || currentUserRes.success === true;
          userId = currentUserRes.data && currentUserRes.data.id;
          if (!isSuccess || !userId) {
            Toast.fail(currentUserRes.msg || "Login session expired. Please log in again.");
            this.goToLogin();
            return false;
          }
          setScheduleAuthUserId(userId);
        }
        this.requesterUserId = userId;
        return true;
      }
      this.anonymousEmployeeName = this.$route.query.employeeName || "";
      this.anonymousDeptId = this.$route.query.hotelDeptId || null;
      this.anonymousDeptName = this.$route.query.deptName || "";
      if (!this.anonymousEmployeeName || !this.anonymousDeptId) {
        Toast.fail("Please enter your name and select a department first.");
        this.goBackToForm();
        return false;
      }
      return true;
    },
    async fetchRecords(page) {
      if (page) {
        this.pageNum = page;
      }
      this.loading = true;
      try {
        const form = {
          hotelNo: this.hotelNo,
          pageNum: this.pageNum,
          pageSize: this.pageSize,
        };
        if (this.requesterUserId) {
          form.requesterUserId = this.requesterUserId;
        } else {
          form.employeeName = this.anonymousEmployeeName;
          form.hotelDeptId = this.anonymousDeptId;
        }
        const res = await scheduleRecordList(form);
        const isSuccess = Number(res.status) === 0 || res.success === true;
        if (isSuccess && res.data) {
          this.records = res.data.list || [];
          this.total = parseInt(res.data.total || 0, 10);
        } else {
          Toast.fail(res.msg || "Failed to load records.");
        }
      } catch (e) {
        Toast.fail("Failed to load records.");
      } finally {
        this.loading = false;
      }
    },
    requestTypeLabel(type) {
      const map = {
        day_off: "Day Off",
        early_leave: "Early Leave",
        vacation: "Vacation",
        overtime: "Sign Up for Overtime",
      };
      return map[type] || type || "-";
    },
    approvalStatusLabel(status) {
      const map = {
        pending: "Pending",
        approved: "Approved",
        rejected: "Rejected",
      };
      return map[status] || "Pending";
    },
    deliveryStatusLabel(status) {
      const map = {
        pending: "Pending",
        sent: "Sent",
        failed: "Failed",
        no_recipient: "No Recipient",
      };
      return map[status] || status || "-";
    },
    approvalStatusClass(status) {
      return `status-${status || 'pending'}`;
    },
    deliveryStatusClass(status) {
      return `delivery-${status || 'pending'}`;
    },
    formatDateRange(startDate, endDate) {
      if (!startDate) return "-";
      if (endDate && endDate !== startDate) {
        return `${startDate} - ${endDate}`;
      }
      return startDate;
    },
    formatTimeRange(startTime, endTime) {
      if (!startTime) return "-";
      return endTime ? `${startTime} - ${endTime}` : startTime;
    },
    formatSubmitTime(time) {
      if (!time) return "-";
      return moment(time).format("MMM DD, YYYY h:mm A");
    },
    buildFormQuery() {
      const query = {
        hotel: this.hotelNo,
      };
      if (this.requesterUserId) {
        if (this.$route.query.user) {
          query.user = this.requesterUserId;
        } else {
          query.login = "1";
        }
      }
      return query;
    },
    goBackToForm() {
      this.$router.push({
        name: "ScheduleRequest",
        query: this.buildFormQuery(),
      });
    },
    goToLogin() {
      this.$router.replace({
        name: "ScheduleLogin",
        query: {
          hotel: this.hotelNo,
        },
      });
    },
  },
};
</script>

<style lang="less" scoped>
.records-page {
  min-height: 100vh;
  padding-bottom: 24px;
}

.header {
  background: #1989fa;
  color: #fff;
  padding: 14px 16px 12px;

  .header-hotel {
    display: flex;
    align-items: center;
  }

  .hotel-logo {
    width: 32px;
    height: 32px;
    object-fit: contain;
    margin-right: 8px;
    border-radius: 4px;
    background: #fff;
    padding: 2px;
    flex-shrink: 0;
  }

  .hotel-name {
    font-size: 14px;
    font-weight: 600;
  }

  .header-title {
    font-size: 17px;
    font-weight: 600;
    text-align: center;
    margin-top: 8px;
  }
}

.summary {
  margin: 16px 16px 0;
  padding: 12px 14px;
  background: #fff7e8;
  border-radius: 10px;
  color: #8a5a00;
  font-size: 13px;
}

.records-content {
  padding: 16px;
}

.record-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  margin-bottom: 14px;
}

.record-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
}

.record-type {
  font-size: 16px;
  font-weight: 600;
  color: #222;
}

.status-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-end;
}

.status-tag {
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
}

.status-pending {
  background: #ecf5ff;
  color: #409eff;
}

.status-approved {
  background: #f0f9eb;
  color: #67c23a;
}

.status-rejected {
  background: #fef0f0;
  color: #f56c6c;
}

.delivery-pending {
  background: #f4f4f5;
  color: #909399;
}

.delivery-sent {
  background: #f0f9eb;
  color: #67c23a;
}

.delivery-failed {
  background: #fef0f0;
  color: #f56c6c;
}

.delivery-no_recipient {
  background: #fdf6ec;
  color: #e6a23c;
}

.record-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  font-size: 13px;
  color: #666;
  padding: 6px 0;

  span:first-child {
    color: #999;
    min-width: 74px;
  }

  span:last-child {
    text-align: right;
    color: #333;
  }

  .notes {
    white-space: pre-wrap;
  }
}

.action-area {
  padding: 0 16px;
}
</style>